import { atom } from "jotai";

// 회원가입 단계 (배열로 관리, 현재 step = 마지막 요소)
export const stepStackAtom = atom<number[]>([1]);

// 현재 step (읽기 전용)
export const stepAtom = atom((get) => {
    const stack = get(stepStackAtom);
    return stack[stack.length - 1] ?? 1;
});

// /sign-up URL 에 쌓인 history pushState entry 개수 (초기 nav 미포함).
//   pushStep 마다 stepStack 이 1 늘고 popstate pop 마다 1 줄어 — stepStack.length-1 과 항상 일치.
//   Step7(중복가입) → 로그인 이동 시 모든 /sign-up entry 를 pop 하는 데 사용.
export const signUpHistoryDepthAtom = atom((get) => get(stepStackAtom).length - 1);

// SignUp 흐름에서 /signin 으로 빠져나갈 때 popstate cascade 동안 popstate handler 를 suppress 하기 위한 flag.
//   exit 시작 시 setSignUpExiting(true), 다음 /sign-up 마운트 시 false 로 reset.
let _isExitingSignUp = false;
export const isExitingSignUp = () => _isExitingSignUp;
export const setSignUpExiting = (v: boolean) => { _isExitingSignUp = v; };

// step 이동 (배열에 push + 브라우저 히스토리에도 push하여 물리 뒤로가기 지원)
export const pushStepAtom = atom(
    null,
    (get, set, nextStep: number) => {
        const stack = get(stepStackAtom);
        set(stepStackAtom, [...stack, nextStep]);
        history.pushState({ signUpStep: nextStep }, "");
    }
);

// 뒤로가기 (배열에서 pop, pop 후의 step 반환. 더 이상 뒤로 갈 수 없으면 null)
export const popStepAtom = atom(
    null,
    (get, set): number | null => {
        const stack = get(stepStackAtom);
        if (stack.length <= 1) return null;

        const newStack = stack.slice(0, -1);
        set(stepStackAtom, newStack);
        return newStack[newStack.length - 1];
    }
);

// 약관 동의 항목
export interface TermsConsentItem {
    termsId: number;
    termsVersion: string;
}

// SNS에서 제공받은 필드 추적
export interface SnsProvidedFields {
    birth: boolean;
    gender: boolean;
    name: boolean;
    email: boolean;
    ctn: boolean;
}

// 회원가입 데이터 타입
export interface SignUpData {
    // 사전 제공 값 (signup/index 참조)
    serviceId: string;
    provider: "EMAIL" | "KAKAO" | "NAVER" | "APPLE" | "GOOGLE" | "LINE";
    providerUserId:string;
    snsLogin: boolean;
    snsProvidedFields: SnsProvidedFields;  // SNS에서 제공받은 필드 추적
    deviceId: string;
    countryCd: string;
    locale: string;
    os: string;

    // Step1: 개인정보 수집 동의 약관
    termsConsents: TermsConsentItem[];          // 약관 리스트

    // Step2: 생년월일/성별
    birth: string;                              // yyyyMMdd
    gender: "MALE" | "FEMALE" | "OTHER" | "";   // 성별
    parentConsent: boolean;                     // 14세 미만 부모 동의 여부

    // Step3, 4: 본인인증 또는 OTP
    name: string;           // 이름
    telecom: string;        // 통신사
    ctn: string;            // 전화번호
    ctnCountryCd: string;   // 전화번호 국가코드
    ctnCountry: string;     // 전화번호 ISO2 국가 ("US") — SNS 원본 파싱 시에만 채워짐. Step4 국가 선택 초기값
    nationality: "KOREAN" | "FOREIGNER";  // KR 다날 내/외국인 (genderDigit 1~4 / 5~8) — 인증 전용

    // 14세 미만 보호자 본인인증 (다날 5항목) — 인증 전용, 가입 데이터엔 미전송
    parentName: string;     // 보호자 이름
    parentTelecom: string;  // 보호자 통신사
    parentBirth: string;    // 보호자 생년월일 yyyyMMdd
    parentGender: "MALE" | "FEMALE" | "";  // 보호자 성별
    parentCtn: string;      // 보호자 전화번호
    parentNationality: "KOREAN" | "FOREIGNER";  // 보호자 내/외국인 — 인증 전용

    // 본인인증 결과 (다날/포트원)
    identityVerificationId: string;        // 가입자(미성년자/본인) 포트원 본인인증 ID
    parentIdentityVerificationId: string;  // 14세 미만 보호자 포트원 본인인증 ID

    // Step6: 계정 정보
    email: string;          // 이메일
    password: string;       // 비밀번호
    nickname: string;       // 닉네임

    // Step6: SNS 프로필 이미지
    //   snsProfileImgUrl 은 SNS 가 내려준 원본 URL, useSnsProfileImg 는 사용자의 사용 여부 선택.
    //   둘 다 화면 상태용이라 가입 요청에는 보내지 않고, 사용에 동의한 경우에만
    //   profileImgUrl 이라는 이름으로 payload 에 실어 서버가 다운로드·저장하도록 한다.
    snsProfileImgUrl: string;
    useSnsProfileImg: boolean;

    // 가입 연령 정책 (서버 판정 — /auth/signup-policy)
    //   policyEnabled=false 면 기존 흐름(14세 미만 체크박스 + 보호자 다날) 그대로 동작한다.
    policyEnabled: boolean;     // 이 서비스에 가입 연령 정책 적용
    idvEnabled: boolean;        // 본인인증(다날) 단계 사용 — false 면 휴대폰 인증(SMS)만
    guardianRequired: boolean;  // 법정대리인 동의 대상 (서버 판정 GUARDIAN_REQUIRED)
    policyBlockReason: string;  // 가입 불가 사유 (MINOR_NOT_ALLOWED / AGE_BELOW_SERVICE_MIN)
    consentAge: number;         // 법정대리인 동의 연령 (안내 문구용)
    infoConfirmed: boolean;     // "입력한 정보가 본인의 실제 정보임을 확인합니다"
    verifyId: number | null;    // 휴대폰 인증 ID (SMS OTP 결과) — 가입 요청에 전달해 서버가 번호 인증을 확인

    // 법정대리인(보호자) 정보 — 동의 요청 문자 발송·원장 기록용 (가입 요청 guardian 으로 전달)
    guardianName: string;
    guardianRelationship: "FATHER" | "MOTHER" | "LEGAL_GUARDIAN" | "OTHER" | "";
    guardianCtn: string;
    guardianCtnCountryCd: string;
}

// 초기값
export const initialSignUpData: SignUpData = {
    // 사전 제공 값
    serviceId: "",
    provider: "EMAIL",      // 사용자 정보 제공자 - "EMAIL" | "KAKAO" | "NAVER" | "APPLE" | "GOOGLE"
    providerUserId: "",
    snsLogin: false,
    snsProvidedFields: {    // SNS에서 제공받은 필드 추적
        birth: false,
        gender: false,
        name: false,
        email: false,
        ctn: false,
    },
    deviceId: "",           // 디바이스 아이디 (민감 정보)
    countryCd: "KR",        // 사용자의 국가 - "KR" | "EN" | "JP"
    locale: "ko",           // 사용자의 언어 - "ko" | "en" | "ja"
    os: "etc",              // 사용자의 운영체제

    // step 2
    birth: "",              // yyyyMMdd - 없거나 어리면 패스
    gender: "",             // "MALE" | "FEMALE" | "OTHER" | ""
    parentConsent: false,   // 14세 미만 부모 동의 여부

    // step 3 & step 4
    name: "",               // 이름
    telecom: "",            // 통신사
    ctn: "",                // 전화번호 '-' 없음 (민감 정보)
    ctnCountryCd: "",        // 국가전화코드 (비어있으면 디바이스 국가 기준으로 초기화)
    ctnCountry: "",         // 전화번호 ISO2 국가 (SNS 원본 파싱 결과)
    nationality: "KOREAN",  // KR 다날 내/외국인 (기본: 내국인)

    // 14세 미만 보호자 본인인증 (다날 5항목)
    parentName: "",         // 보호자 이름
    parentTelecom: "",      // 보호자 통신사
    parentBirth: "",        // 보호자 생년월일 yyyyMMdd
    parentGender: "",       // 보호자 성별
    parentCtn: "",          // 보호자 전화번호 (미성년자 번호와 동일 입력 방지에도 사용)
    parentNationality: "KOREAN",  // 보호자 내/외국인 (기본: 내국인)

    // 본인인증 결과 (다날/포트원)
    identityVerificationId: "",        // 가입자 포트원 본인인증 ID
    parentIdentityVerificationId: "",  // 보호자 포트원 본인인증 ID

    // step 6 - 유저 정보
    email: "",              // ID
    password: "",           // PW (민감 정보)
    nickname: "",           // 닉네임

    // step 6 - SNS 프로필 이미지
    snsProfileImgUrl: "",   // SNS 가 제공한 프로필 이미지 원본 URL
    useSnsProfileImg: true, // 사용 여부 (SNS 프로필 제공에 이미 동의했으므로 기본 사용)

    // 가입 연령 정책
    policyEnabled: false,
    idvEnabled: true,
    guardianRequired: false,
    policyBlockReason: "",
    consentAge: 14,
    infoConfirmed: false,
    verifyId: null,

    // 법정대리인(보호자) 정보
    guardianName: "",
    guardianRelationship: "",
    guardianCtn: "",
    guardianCtnCountryCd: "",

    // step 1
    termsConsents: []       // 수락한 약관 리스트
};

// 회원가입 데이터 atom
export const signUpDataAtom = atom<SignUpData>(initialSignUpData);

// 회원가입 데이터 초기화 함수
export const resetSignUpData = atom(
    null,
    (_get, set) => {
        set(signUpDataAtom, initialSignUpData);
        set(stepStackAtom, [1]);
    }
);